import { useCallback, useEffect, useRef, useState } from 'react';
import { getFileThumbnailBlob, setFileThumbnailBlob } from '../../db/fileThumbnails';
import { FILE_THUMBNAIL_TTL_MS } from '../../db/schema';
import * as aes from '../../lib/aes';
import { FileService } from '../../services/fileApiService';
import { FunctionService } from '../../services/functionService';
import { useAppStore } from '../../store/store';
import { isFileThumbnailSupported } from '../../utils/utils';

const fileService = new FileService();
const functionService = new FunctionService();

/** Max concurrent thumbnail fetches (getFileForViewer + getFileBlob) to avoid overwhelming the network. */
const CONCURRENT_THUMBNAIL_FETCHES = 5;
let thumbnailFetchInFlight = 0;
const thumbnailFetchQueue: Array<() => void> = [];

function acquireThumbnailSlot(): Promise<void> {
  if (thumbnailFetchInFlight < CONCURRENT_THUMBNAIL_FETCHES) {
    thumbnailFetchInFlight += 1;
    return Promise.resolve();
  }
  return new Promise<void>((resolve) => {
    thumbnailFetchQueue.push(() => {
      thumbnailFetchInFlight += 1;
      resolve();
    });
  });
}

function releaseThumbnailSlot(): void {
  thumbnailFetchInFlight -= 1;
  if (thumbnailFetchQueue.length > 0) {
    const next = thumbnailFetchQueue.shift()!;
    next();
  }
}

/**
 * Decrypts an encrypted file thumbnail blob using AES CBC.
 * File thumbnails are always JPEG images, so we set the type accordingly.
 */
async function decryptFileThumbnailBlob(blob: Blob): Promise<Blob> {
  try {
    // WebAssembly crypto module is hydrated from environment
    const wasmBytesString = import.meta.env.VITE_APP_WASM_MODULE_BYTES as string;
    const wasmBytes = wasmBytesString.split(',').map(Number);
    // Spin up WASM instance and get buffers/keys
    const { instance } = await WebAssembly.instantiate(new Uint8Array(wasmBytes));
    const memory = new Uint8Array((instance.exports as any).memory.buffer);
    const _x = (instance.exports as any).x();
    const _y = (instance.exports as any).y();
    const x = memory.subarray(_x, _x + 32);
    const y = memory.subarray(_y, _y + 16);
    const aesCbc = new aes.ModeOfOperation.cbc(x, y);

    // Transform blob to Uint8Array and decrypt
    const fileArrayBuffer = await new Response(blob).arrayBuffer();
    const encryptedBytes = new Uint8Array(fileArrayBuffer);
    const bytesToRemove = encryptedBytes[encryptedBytes.length - 1]!;
    // Remove padding indicator byte and decrypt
    const decryptedBytes = aesCbc.decrypt(encryptedBytes.slice(0, -1));
    // Remove PKCS padding for final blob
    // File thumbnails are always JPEG images
    const decrypted = new Blob([decryptedBytes.slice(0, bytesToRemove * -1)], {
      type: 'image/jpeg',
    });
    return decrypted;
  } catch (e) {
    console.error('Failed to decrypt file thumbnail blob:', e);
    throw e;
  }
}

/**
 * Hook to manage file thumbnails with caching.
 * Persisted state (token + expiresAt) is rehydrated from localStorage on load; we then call
 * getFileBlob(token) once per file to get the image and create a new object URL (blob URLs
 * don't survive refresh). So fileviewer is called once per thumbnail on refresh by design.
 *
 * @param fileId - The file ID (optional, if not provided returns null)
 * @param fileExtension - The file extension (e.g., ".pdf")
 * @returns Object with fileThumbnailUrl, fileThumbnailLoading, and fileThumbnailError
 */
export function useFileThumbnail(
  fileId: number | undefined | null,
  fileExtension: string,
): {
  fileThumbnailUrl: string | null;
  fileThumbnailLoading: boolean;
  fileThumbnailError: Error | null;
} {
  const [fileThumbnailUrl, setFileThumbnailUrl] = useState<string | null>(null);
  const [fileThumbnailLoading, setFileThumbnailLoading] = useState<boolean>(false);
  const [fileThumbnailError, setFileThumbnailError] = useState<Error | null>(null);

  const TWENTY_FOUR_HOURS_MS = FILE_THUMBNAIL_TTL_MS;

  const {
    setFileThumbnail,
    getFileThumbnail,
    clearFileThumbnail,
    isFileThumbnailValid,
    getFileThumbnailSkipUntil,
    setFileThumbnailSkipUntil,
    clearFileThumbnailSkipForFile,
    restoreFileThumbnailFromToken,
  } = useAppStore();

  // Don't start thumbnail fetches when not authenticated (avoids 401 storm on logout)
  const token = useAppStore((s) => s.token);
  // Subscribe to cache entry so when restoreFileThumbnailFromToken completes we get the new URL
  const cachedEntry = useAppStore((s) => (fileId ? s.filesThumbnails?.[fileId] : undefined));
  // Subscribe to skipUntil so when it's cleared after restore failure we re-run effect and refetch
  const skipUntilForFile = useAppStore((s) =>
    fileId ? s.fileThumbnailSkipUntil?.[fileId] : undefined,
  );

  // Ref to track if component is mounted (for cleanup)
  const isMountedRef = useRef<boolean>(true);
  // Ref to track current request to avoid race conditions
  const currentRequestRef = useRef<number | null>(null);

  /**
   * Fetches a file thumbnail for the given file.
   * Checks cache first, then fetches from API if needed.
   */
  const fetchFileThumbnail = useCallback(
    async (id: number, extension: string) => {
      // Check if format is supported - do this FIRST to avoid unnecessary API calls
      if (!isFileThumbnailSupported(extension)) {
        setFileThumbnailUrl(null);
        setFileThumbnailLoading(false);
        setFileThumbnailError(null);
        return;
      }

      // Check cache first
      const cached = getFileThumbnail(id);
      if (cached && isFileThumbnailValid(id)) {
        if (isMountedRef.current) {
          setFileThumbnailUrl(cached.fileThumbnailUrl);
          setFileThumbnailLoading(false);
          setFileThumbnailError(null);
        }
        return;
      }

      // Clear invalid cache entry
      if (cached && !isFileThumbnailValid(id)) {
        clearFileThumbnail(id);
      }

      // Skip fetch if we already attempted within the last 24 hours, unless we have a
      // rehydrated entry (token+expiresAt, no URL) that we can restore from token
      const skipUntil = getFileThumbnailSkipUntil(id);
      const canRestoreFromPersisted =
        cached && cached.token && cached.expiresAt && !cached.fileThumbnailUrl;
      if (skipUntil !== undefined && !canRestoreFromPersisted) {
        if (isMountedRef.current) {
          setFileThumbnailUrl(null);
          setFileThumbnailLoading(false);
          setFileThumbnailError(null);
        }
        return;
      }

      // Check if we have a persisted token that's still valid (within 24 hours)
      // This allows us to skip the getFileForViewer API call
      const persistedEntry = cached && cached.token && cached.expiresAt ? cached : null;
      const now = Date.now();
      const hasValidPersistedToken =
        persistedEntry &&
        persistedEntry.expiresAt > now &&
        persistedEntry.expiresAt <= now + TWENTY_FOUR_HOURS_MS &&
        !persistedEntry.fileThumbnailUrl; // Only if we don't have a blob URL yet

      // Set loading state
      if (isMountedRef.current) {
        setFileThumbnailLoading(true);
        setFileThumbnailError(null);
      }

      // Track this request
      const requestId = Date.now();
      currentRequestRef.current = requestId;

      // Limit concurrent thumbnail fetches so we run at most 5 in parallel
      await acquireThumbnailSlot();
      if (!isMountedRef.current || currentRequestRef.current !== requestId) {
        releaseThumbnailSlot();
        return;
      }

      try {
        let token: string;
        let expirationAt: number;

        if (hasValidPersistedToken) {
          // Optimization: Use persisted token directly, skip getFileForViewer API call
          token = persistedEntry.token;
          expirationAt = persistedEntry.expiresAt;
        } else {
          // Step 1: Get JWT token with thumb=true
          const tokenResponse = await fileService.getFileForViewer(id, { thumb: true });

          // Check if request was cancelled or component unmounted
          if (!isMountedRef.current || currentRequestRef.current !== requestId) {
            return;
          }

          if (!tokenResponse.token) {
            throw new Error('Failed to get file thumbnail token');
          }

          token = tokenResponse.token;
          // Use expirationAt from token response if available, otherwise default to 24 hours
          expirationAt = tokenResponse.expirationAt
            ? tokenResponse.expirationAt * 1000 // Convert from seconds to milliseconds
            : Date.now() + 24 * 60 * 60 * 1000; // 24 hours from now
        }

        // Step 2: Get encrypted blob from fileviewer
        const encryptedBlob = await functionService.getFileBlob(token);

        // Check if request was cancelled or component unmounted
        if (!isMountedRef.current || currentRequestRef.current !== requestId) {
          return;
        }

        // Empty blob = no preview (e.g. SVG); mark as processed and skip refetch for 24h
        if (!encryptedBlob || encryptedBlob.size === 0) {
          setFileThumbnailSkipUntil(id, Date.now() + TWENTY_FOUR_HOURS_MS);
          if (isMountedRef.current && currentRequestRef.current === requestId) {
            setFileThumbnailUrl(null);
            setFileThumbnailLoading(false);
            setFileThumbnailError(null);
          }
          return;
        }

        // Step 3: Decrypt the blob (file thumbnails are encrypted like regular files)
        const decryptedBlob = await decryptFileThumbnailBlob(encryptedBlob);

        // Check if request was cancelled or component unmounted after decryption
        if (!isMountedRef.current || currentRequestRef.current !== requestId) {
          return;
        }

        // Persist decrypted blob to IndexedDB for faster restore on next load
        setFileThumbnailBlob(id, decryptedBlob).catch(() => {});

        // Step 4: Create object URL from decrypted blob
        const objectUrl = URL.createObjectURL(decryptedBlob);

        // Step 5: Save to cache (expirationAt and token were already determined above)
        setFileThumbnail(id, {
          fileThumbnailUrl: objectUrl,
          expiresAt: expirationAt,
          token: token,
        });
        setFileThumbnailSkipUntil(id, Date.now() + TWENTY_FOUR_HOURS_MS);

        // Update state
        if (isMountedRef.current && currentRequestRef.current === requestId) {
          setFileThumbnailUrl(objectUrl);
          setFileThumbnailLoading(false);
          setFileThumbnailError(null);
        }
      } catch (err: any) {
        // Check if request was cancelled or component unmounted
        if (!isMountedRef.current || currentRequestRef.current !== requestId) {
          return;
        }

        // Do not set skip for auth errors (401/403) so we refetch after login; clear any existing skip for this file
        const statusCode = err?.response?.status;
        const isAuthError = statusCode === 401 || statusCode === 403;
        if (isAuthError) {
          clearFileThumbnailSkipForFile(id);
        } else {
          setFileThumbnailSkipUntil(id, Date.now() + TWENTY_FOUR_HOURS_MS);
        }

        // Handle specific error cases
        if (statusCode === 400) {
          // Format not supported - this is expected for some files
          setFileThumbnailUrl(null);
          setFileThumbnailLoading(false);
          setFileThumbnailError(null); // Don't treat as error, just unsupported
        } else if (statusCode === 500) {
          // Server error during file thumbnail generation
          const error = new Error('File thumbnail generation failed');
          setFileThumbnailUrl(null);
          setFileThumbnailLoading(false);
          setFileThumbnailError(error);
        } else {
          // Other errors (network, etc.)
          setFileThumbnailUrl(null);
          setFileThumbnailLoading(false);
          setFileThumbnailError(
            err instanceof Error ? err : new Error('Failed to fetch file thumbnail'),
          );
        }
      } finally {
        releaseThumbnailSlot();
      }
    },
    [
      getFileThumbnail,
      isFileThumbnailValid,
      clearFileThumbnail,
      setFileThumbnail,
      getFileThumbnailSkipUntil,
      setFileThumbnailSkipUntil,
      clearFileThumbnailSkipForFile,
      restoreFileThumbnailFromToken,
    ],
  );

  // Fetch file thumbnail when fileId or extension changes
  useEffect(() => {
    isMountedRef.current = true;

    // Early return if no fileId or format not supported - avoid any API calls
    if (!fileId || !isFileThumbnailSupported(fileExtension)) {
      setFileThumbnailUrl(null);
      setFileThumbnailLoading(false);
      setFileThumbnailError(null);
      return;
    }

    // Check cache first (synchronous check) - only use when we have a displayable URL
    const cached = getFileThumbnail(fileId);
    if (cached && isFileThumbnailValid(fileId) && cached.fileThumbnailUrl) {
      setFileThumbnailUrl(cached.fileThumbnailUrl);
      setFileThumbnailLoading(false);
      setFileThumbnailError(null);
      return;
    }

    // Don't fetch when not authenticated (stops 401 storm when logout clears token and effects re-run)
    if (!token) {
      setFileThumbnailUrl(null);
      setFileThumbnailLoading(false);
      setFileThumbnailError(null);
      return;
    }

    // Try IndexedDB first: if we have the blob there, use it and do NOT call fileviewer (avoids 401 with expired tokens)
    setFileThumbnailLoading(true);
    getFileThumbnailBlob(fileId).then((blob) => {
      if (!isMountedRef.current) return;
      if (blob) {
        const objectUrl = URL.createObjectURL(blob);
        setFileThumbnail(fileId, {
          fileThumbnailUrl: objectUrl,
          expiresAt: Date.now() + FILE_THUMBNAIL_TTL_MS,
          token: '',
        });
        setFileThumbnailUrl(objectUrl);
        setFileThumbnailLoading(false);
        setFileThumbnailError(null);
        return;
      }
      // No blob in IndexedDB: only then consider rehydrated token or full fetch
      const hasRehydratedTokenNoUrl =
        cached &&
        cached.token &&
        cached.expiresAt &&
        !cached.fileThumbnailUrl &&
        isFileThumbnailValid(fileId);
      if (hasRehydratedTokenNoUrl) {
        restoreFileThumbnailFromToken(fileId, cached.token, cached.expiresAt).catch(() => {});
      } else {
        fetchFileThumbnail(fileId, fileExtension);
      }
    });

    // Cleanup function - only mark as unmounted, don't revoke URLs
    // The cache will handle URL revocation when entries are removed
    return () => {
      isMountedRef.current = false;
      currentRequestRef.current = null;
    };
  }, [
    fileId,
    fileExtension,
    token,
    fetchFileThumbnail,
    getFileThumbnail,
    isFileThumbnailValid,
    restoreFileThumbnailFromToken,
    skipUntilForFile,
  ]);

  // When store cache gets a URL (e.g. after restore from token), sync to local state
  useEffect(() => {
    if (fileId && cachedEntry?.fileThumbnailUrl && isFileThumbnailValid(fileId)) {
      setFileThumbnailUrl(cachedEntry.fileThumbnailUrl);
      setFileThumbnailLoading(false);
      setFileThumbnailError(null);
    }
  }, [fileId, cachedEntry?.fileThumbnailUrl, isFileThumbnailValid]);

  return {
    fileThumbnailUrl,
    fileThumbnailLoading,
    fileThumbnailError,
  };
}
